<template>
  <div class="step-localization">
    <div v-if="!store.programId" class="state">{{ $t('bo.programs.creation.localizationNeedProgram') }}</div>

    <template v-else>
      <div class="loc-head">
        <div>
          <h3 class="loc-title">{{ $t('bo.programs.localizationTitle') }}</h3>
          <p class="loc-hint">{{ $t('bo.programs.localizationHint') }}</p>
        </div>
        <button
          type="button"
          class="btn-generate"
          :disabled="localizing || store.isArchivedProgram"
          @click="generate"
        >
          {{ localizing ? $t('bo.programs.generating') : $t('bo.programs.generateTranslations') }}
        </button>
      </div>

      <div v-if="localesError" class="error-inline">{{ localesError }}</div>

      <div v-if="otherLocales.length" class="loc-tabs">
        <button
          v-for="l in otherLocales"
          :key="l.code"
          type="button"
          class="loc-tab"
          :class="{ active: activeLocale === l.code }"
          @click="activeLocale = l.code"
        >
          {{ l.label || l.code }}
          <span v-if="isAuto(l.code)" class="auto-badge">{{ $t('bo.programs.autoBadge') }}</span>
        </button>
      </div>

      <p v-else-if="!loadingLocales" class="state muted">{{ $t('bo.programs.creation.noOtherLocales') }}</p>

      <template v-if="activeLocale && translations[activeLocale]">
        <div class="form-group">
          <label>{{ $t('bo.programs.labelName') }}</label>
          <input
            v-model="translations[activeLocale].name"
            type="text"
            class="input"
            maxlength="200"
            :disabled="store.isArchivedProgram"
          />
        </div>
        <div class="form-group">
          <label>{{ $t('bo.programs.labelObjective') }}</label>
          <textarea
            v-model="translations[activeLocale].objective"
            class="textarea"
            rows="2"
            maxlength="1000"
            :disabled="store.isArchivedProgram"
          />
        </div>
        <div class="form-group">
          <label>{{ $t('bo.common.description') }}</label>
          <textarea
            v-model="translations[activeLocale].description"
            class="textarea"
            rows="3"
            maxlength="2000"
            :disabled="store.isArchivedProgram"
          />
        </div>
      </template>

      <div v-if="store.programStatus !== 'archived'" class="lifecycle">
        <div class="lifecycle-label">{{ $t('bo.programs.lifecycleActions') }}</div>
        <p class="lifecycle-hint">{{ $t('bo.programs.creation.publishHint') }}</p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAdminProgramCreationStore } from '@/stores/adminProgramCreation'
import adminProgramService from '@/services/adminProgramService'
import translationService from '@/services/translationService'

const { t } = useI18n()
const store = useAdminProgramCreationStore()

const locales = ref([])
const defaultLocale = ref('en')
const translations = ref({})
const original = ref({})
const activeLocale = ref('')
const localizing = ref(false)
const loadingLocales = ref(false)
const localesError = ref('')

const otherLocales = computed(() => locales.value.filter((l) => l.code !== defaultLocale.value))

const isAuto = (code) => !!translations.value[code]?.is_auto_generated

const loadLocales = async () => {
  loadingLocales.value = true
  localesError.value = ''
  try {
    const data = await translationService.getLocales()
    locales.value = Array.isArray(data) ? data : []
    defaultLocale.value = locales.value.find((l) => l.is_default)?.code || 'en'
  } catch {
    locales.value = []
    localesError.value = t('bo.programs.creation.failedLoadLocales')
  } finally {
    loadingLocales.value = false
  }
}

const loadTranslations = async () => {
  if (!store.programId) return
  try {
    const res = await adminProgramService.getTranslations(store.programId)
    const rows = res.data?.data || []
    const map = {}
    rows.forEach((r) => {
      map[r.locale] = {
        name: r.name || '',
        objective: r.objective || '',
        description: r.description || '',
        is_auto_generated: !!r.is_auto_generated,
      }
    })
    translations.value = map
    original.value = JSON.parse(JSON.stringify(map))
  } catch {
    // non-blocking
  }
}

const generate = async () => {
  if (!store.programId) return
  localizing.value = true
  store.error = null
  try {
    await adminProgramService.localize(store.programId)
    await loadTranslations()
    if (!activeLocale.value) {
      activeLocale.value = otherLocales.value[0]?.code || ''
    }
  } catch (e) {
    store.error = e.response?.data?.message || t('bo.programs.failedLocalize')
  } finally {
    localizing.value = false
  }
}

const saveChangedTranslations = async () => {
  if (!store.programId || store.isArchivedProgram) return
  for (const l of otherLocales.value) {
    const cur = translations.value[l.code]
    const ori = original.value[l.code]
    const changed =
      cur &&
      (!ori || cur.name !== ori.name || cur.objective !== ori.objective || cur.description !== ori.description)
    if (changed && (cur.name?.trim() || cur.objective?.trim() || cur.description?.trim())) {
      await adminProgramService.saveTranslation(store.programId, l.code, {
        name: cur.name?.trim() || null,
        objective: cur.objective?.trim() || null,
        description: cur.description?.trim() || null,
      })
    }
  }
  original.value = JSON.parse(JSON.stringify(translations.value))
}

defineExpose({ saveChangedTranslations })

onMounted(async () => {
  await loadLocales()
  await loadTranslations()
  activeLocale.value = otherLocales.value[0]?.code || ''
})

watch(
  () => store.programId,
  async (id) => {
    if (id) {
      await loadTranslations()
      if (!activeLocale.value) activeLocale.value = otherLocales.value[0]?.code || ''
    }
  }
)
</script>

<style scoped>
.step-localization {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.loc-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.loc-title {
  margin: 0 0 0.25rem;
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
}

.loc-hint {
  margin: 0;
  font-size: 0.85rem;
  color: #6b7280;
}

.btn-generate {
  padding: 0.45rem 0.9rem;
  border: 1px solid #3b82f6;
  background: white;
  color: #2563eb;
  border-radius: 0.375rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}

.btn-generate:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loc-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.loc-tab {
  padding: 0.35rem 0.75rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 9999px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.loc-tab.active {
  border-color: #3b82f6;
  color: #2563eb;
  background: #eff6ff;
}

.auto-badge {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #92400e;
  background: #fef3c7;
  padding: 0.05rem 0.35rem;
  border-radius: 9999px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.input,
.textarea {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;
}

.lifecycle {
  margin-top: 0.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.lifecycle-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
  margin-bottom: 0.35rem;
}

.lifecycle-hint {
  margin: 0;
  font-size: 0.85rem;
  color: #6b7280;
}

.state {
  padding: 2rem;
  text-align: center;
  color: #6b7280;
}

.state.muted {
  padding: 0.5rem;
  text-align: left;
}

.error-inline {
  color: #dc2626;
  font-size: 0.875rem;
}
</style>
